import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { useStore } from 'vuex'
import { buildFundraisingTargetUrl } from 'foundation/js/utils/fundraisingFormAnchors'

const MOBILE_MEDIA_QUERY = '(max-width: 768px)'
const VIEWPORT_GUTTER = 16
const QR_CODE_OVERLAY = 'FundraisingQrCodeOverlay'

let instanceCounter = 0

export default {
    name: 'PersonalFundraisingDashboardMenu',
    props: {
        editUrl: {
            type: String,
            default: null
        },
        campaignId: {
            type: String,
            default: null
        },
        qrCodeUrl: {
            type: String,
            default: null
        },
        qrCodeImage: {
            type: String,
            default: null
        },
        livestreamUrl: {
            type: String,
            default: null
        },
        isMemorial: {
            type: Boolean,
            default: false
        },
        triggerLabel: {
            type: String,
            default: 'Flere muligheder'
        },
        updateTargetLabel: {
            type: String,
            default: 'Opdater indsamlingsmål'
        },
        qrCodeLabel: {
            type: String,
            default: 'QR-kode'
        },
        livestreamLabel: {
            type: String,
            default: 'Livestream'
        }
    },
    setup(props) {
        const store = useStore()
        const rootEl = ref(null)
        const triggerEl = ref(null)
        const menuEl = ref(null)
        const isOpen = ref(false)
        const opensAbove = ref(false)
        const horizontalOffset = ref(0)
        const menuId = `kb-personal-fundraising-dashboard-menu-${++instanceCounter}`
        let originalBodyOverflow = null

        // The overlay renders the code, so an image is what makes QR available.
        const hasMenu = computed(
            () => !!props.qrCodeImage || !!props.livestreamUrl
        )

        const fundraisingTargetUrl = computed(() =>
            buildFundraisingTargetUrl(props.editUrl)
        )

        // Mindeindsamlinger have no fundraising target, so the menu leaves the item out.
        const showUpdateTarget = computed(
            () => !props.isMemorial && !!fundraisingTargetUrl.value
        )

        const openQrCodeOverlay = () => {
            closeMenu()
            store.commit('main/set_page_overlay_content_data', {
                qrImgUrlBase: props.qrCodeImage,
                qrPrintPageLink: props.qrCodeUrl,
                qrPrintPageLinkTarget: '_blank',
                fundraisingCampaignId: props.campaignId
            })
            // The trigger: closing the menu unmounts the item focus would return to.
            store.dispatch('main/openPageOverlay', {
                overlayContent: QR_CODE_OVERLAY,
                returnTarget: triggerEl.value
            })
        }

        const menuStyle = computed(() => ({
            transform: horizontalOffset.value
                ? `translateX(${horizontalOffset.value}px)`
                : undefined
        }))

        const getMenuItems = () =>
            Array.from(
                menuEl.value?.querySelectorAll('[role="menuitem"]') || []
            )

        const lockBodyScroll = () => {
            if (
                !window.matchMedia(MOBILE_MEDIA_QUERY).matches ||
                originalBodyOverflow !== null
            ) {
                return
            }

            originalBodyOverflow = document.body.style.overflow
            document.body.style.overflow = 'hidden'
        }

        const unlockBodyScroll = () => {
            if (originalBodyOverflow === null) {
                return
            }

            document.body.style.overflow = originalBodyOverflow
            originalBodyOverflow = null
        }

        const handleViewportResize = () => closeMenu()

        const removeGlobalListeners = () => {
            document.removeEventListener(
                'pointerdown',
                handleOutsidePointerDown
            )
            document.removeEventListener('keydown', handleDocumentKeydown)
            window.removeEventListener('resize', handleViewportResize)
            unlockBodyScroll()
        }

        const closeMenu = (restoreFocus = false) => {
            if (!isOpen.value) {
                return
            }

            isOpen.value = false
            opensAbove.value = false
            horizontalOffset.value = 0
            removeGlobalListeners()

            if (restoreFocus) {
                nextTick(() => triggerEl.value?.focus())
            }
        }

        const handleOutsidePointerDown = (event) => {
            if (!rootEl.value?.contains(event.target)) {
                closeMenu()
            }
        }

        const handleDocumentKeydown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                closeMenu(true)
            }
        }

        const positionMenu = async () => {
            await nextTick()

            if (
                !menuEl.value ||
                window.matchMedia(MOBILE_MEDIA_QUERY).matches
            ) {
                return
            }

            const triggerRect = triggerEl.value.getBoundingClientRect()
            const initialRect = menuEl.value.getBoundingClientRect()
            const spaceBelow = window.innerHeight - triggerRect.bottom
            const spaceAbove = triggerRect.top

            opensAbove.value =
                initialRect.bottom > window.innerHeight - VIEWPORT_GUTTER &&
                spaceAbove > spaceBelow

            await nextTick()

            const menuRect = menuEl.value?.getBoundingClientRect()
            if (!menuRect) {
                return
            }

            if (menuRect.left < VIEWPORT_GUTTER) {
                horizontalOffset.value = VIEWPORT_GUTTER - menuRect.left
            } else if (menuRect.right > window.innerWidth - VIEWPORT_GUTTER) {
                horizontalOffset.value =
                    window.innerWidth - VIEWPORT_GUTTER - menuRect.right
            }
        }

        const openMenu = async () => {
            isOpen.value = true
            document.addEventListener('pointerdown', handleOutsidePointerDown)
            document.addEventListener('keydown', handleDocumentKeydown)
            window.addEventListener('resize', handleViewportResize)
            lockBodyScroll()

            await positionMenu()
            getMenuItems()[0]?.focus()
        }

        const toggleMenu = () => {
            if (isOpen.value) {
                closeMenu(true)
            } else {
                openMenu()
            }
        }

        const handleMenuKeydown = (event) => {
            const menuItems = getMenuItems()
            const currentIndex = menuItems.indexOf(document.activeElement)
            let nextIndex = null

            if (event.key === 'ArrowDown') {
                nextIndex = (currentIndex + 1) % menuItems.length
            } else if (event.key === 'ArrowUp') {
                nextIndex =
                    (currentIndex - 1 + menuItems.length) % menuItems.length
            } else if (event.key === 'Home') {
                nextIndex = 0
            } else if (event.key === 'End') {
                nextIndex = menuItems.length - 1
            }

            if (nextIndex !== null) {
                event.preventDefault()
                menuItems[nextIndex]?.focus()
            }
        }

        const handleFocusout = () => {
            window.setTimeout(() => {
                if (!rootEl.value?.contains(document.activeElement)) {
                    closeMenu()
                }
            }, 0)
        }

        onBeforeUnmount(removeGlobalListeners)

        return {
            closeMenu,
            fundraisingTargetUrl,
            handleFocusout,
            handleMenuKeydown,
            hasMenu,
            horizontalOffset,
            isOpen,
            menuEl,
            menuId,
            menuStyle,
            openQrCodeOverlay,
            opensAbove,
            rootEl,
            showUpdateTarget,
            toggleMenu,
            triggerEl
        }
    }
}
